import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Chip,
    TextField,
    InputAdornment,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Tooltip,
    Avatar,
    Divider,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Warning,
    TrendingUp,
    TrendingDown,
    Inventory,
    Edit,
    History,
    Search,
    CameraAlt as CameraIcon,
} from "@mui/icons-material";
import { StockAdjustmentDialog } from "../../components/inventory/StockAdjustmentDialog";
import { BarcodeScannerDialog } from "../../components/common/BarcodeScannerDialog";
import { useMobilePagination } from "../../hooks/useMobilePagination";
import { MobilePaginationControls } from "../../components/common/MobilePaginationControls";
import { GridColDef } from "@mui/x-data-grid";
import { DataTable } from "../../components/common/DataTable";
import {
    stockService,
    StockInfo,
    LowStockItem,
} from "../../services/stockService";
import { productService } from "../../services/productService";
import { Product } from "../../types";
import { usePermissions } from "../../hooks/usePermissions";

export const StockManagement: React.FC = () => {
    const { canWrite } = usePermissions();
    const [products, setProducts] = useState<Product[]>([]);
    const [stockData, setStockData] = useState<{ [key: string]: StockInfo }>(
        {},
    );
    const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(
        null,
    );
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        currentItems: pagedProducts,
        page: mobilePage,
        totalPages: mobileTotalPages,
        canGoNext: mobileCanGoNext,
        canGoPrev: mobileCanGoPrev,
        goToNext: mobileGoToNext,
        goToPrev: mobileGoToPrev,
        startIndex: mobileStartIndex,
        endIndex: mobileEndIndex,
        total: mobileTotal,
    } = useMobilePagination(products, 10);

    // Improved search: minimum 1 character, debounced for better UX
    const MIN_SEARCH_LENGTH = 1;
    const DEBOUNCE_DELAY = 1000; // 1 second debounce

    const performSearch = useCallback(async (query: string) => {
        try {
            setLoading(true);
            setError("");

            if (query && query.length >= MIN_SEARCH_LENGTH) {
                // Search with API call
                const response = await productService.getProducts({
                    skip: 0,
                    limit: 1000,
                    search: query,
                });

                setProducts((response as any).data || []);

                // Rebuild stock data map from search results
                const stockMap: { [key: string]: StockInfo } = {};
                ((response as any).data || []).forEach((product: Product) => {
                    if (product.stock) {
                        stockMap[product.product_id] = {
                            stock_id: product.product_id,
                            product_id: product.product_id,
                            quantity: product.stock.quantity,
                            reserved_quantity: product.stock.reserved_quantity,
                            min_stock_level:
                                product.stock.min_stock_level || 10,
                            max_stock_level:
                                product.stock.max_stock_level || 1000,
                            available_quantity: Math.max(
                                product.stock.quantity -
                                    product.stock.reserved_quantity,
                                0,
                            ),
                            is_low_stock:
                                product.stock.quantity -
                                    product.stock.reserved_quantity <=
                                (product.stock.min_stock_level || 10),
                        };
                    }
                });

                setStockData(stockMap);

                // Focus search input after results load for better UX
                setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                }, 100);
            } else {
                // Load all products when search is empty or too short
                await loadData();
            }
        } catch (err: any) {
            setError("Failed to search products");
            console.error("Products search error:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounced search effect
    useEffect(() => {
        if (searchTerm.length >= MIN_SEARCH_LENGTH) {
            const timer = setTimeout(() => {
                performSearch(searchTerm);
            }, DEBOUNCE_DELAY);

            return () => clearTimeout(timer);
        } else if (searchTerm.length === 0) {
            // Clear search results when search is empty
            performSearch("");
        }
    }, [searchTerm, performSearch]);

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setSearchTerm(value);
        },
        [],
    );

    const handleClearSearch = useCallback(() => {
        setSearchTerm("");

        // Focus search input after clearing
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 100);
    }, []);

    // Auto-focus search input on component mount
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 500); // Wait for component to fully render

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");

            // Load products with stock data in single optimized API call
            const productsData = await productService.getProducts({
                limit: 1000,
            });

            setProducts((productsData as any).data || []);

            // Build stock data map from products that already include stock
            const stockMap: { [key: string]: StockInfo } = {};
            ((productsData as any).data || []).forEach((product: Product) => {
                if (product.stock) {
                    stockMap[product.product_id] = {
                        stock_id: product.product_id, // Use product_id as stock_id since it's 1:1
                        product_id: product.product_id,
                        quantity: product.stock.quantity,
                        reserved_quantity: product.stock.reserved_quantity,
                        min_stock_level: product.stock.min_stock_level || 10,
                        max_stock_level: product.stock.max_stock_level || 1000,
                        available_quantity: Math.max(
                            product.stock.quantity -
                                product.stock.reserved_quantity,
                            0,
                        ),
                        is_low_stock:
                            product.stock.quantity -
                                product.stock.reserved_quantity <=
                            (product.stock.min_stock_level || 10),
                    };
                }
            });

            setStockData(stockMap);

            // Load low stock items separately (this is a specialized endpoint)
            const lowStockData = await stockService.getLowStockReport();
            setLowStockItems(lowStockData);
        } catch (err: any) {
            setError("Failed to load stock data");
            console.error("Stock data fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    // Refresh data while preserving search state
    const refreshData = useCallback(async () => {
        if (searchTerm && searchTerm.length >= MIN_SEARCH_LENGTH) {
            // If there's an active search, re-run the search to preserve filtered results
            await performSearch(searchTerm);
        } else {
            // Otherwise, reload all data
            await loadData();
        }
    }, [searchTerm, performSearch]);

    const handleAdjustStock = (product: Product) => {
        setSelectedProduct(product);
        setAdjustmentDialogOpen(true);
    };

    const getStockStatus = (product: Product) => {
        const stock = stockData[product.product_id];
        if (!stock) return { color: "default", label: "No Stock Data" };

        const available =
            stock.available_quantity ??
            Math.max(stock.quantity - stock.reserved_quantity, 0);

        if (available <= 0) return { color: "error", label: "Out of Stock" };
        if (available <= stock.min_stock_level)
            return { color: "warning", label: "Low Stock" };
        return { color: "success", label: "In Stock" };
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Product",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight="medium">
                        {params.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        SKU: {params.row.sku}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "stock_status",
            headerName: "Status",
            width: 120,
            renderCell: (params) => {
                const status = getStockStatus(params.row);
                return (
                    <Chip
                        label={status.label}
                        color={status.color as any}
                        size="small"
                        icon={
                            status.color === "error" ? (
                                <Warning />
                            ) : (
                                <Inventory />
                            )
                        }
                    />
                );
            },
        },
        {
            field: "current_stock",
            headerName: "Current Stock",
            width: 130,
            renderCell: (params) => {
                const stock = stockData[params.row.product_id];
                return stock ? (
                    <Typography variant="body2" fontWeight="medium">
                        {stock.quantity} units
                    </Typography>
                ) : (
                    "-"
                );
            },
        },
        {
            field: "reserved_stock",
            headerName: "Reserved",
            width: 100,
            renderCell: (params) => {
                const stock = stockData[params.row.product_id];
                return stock ? (
                    <Typography variant="body2" color="warning.main">
                        {stock.reserved_quantity}
                    </Typography>
                ) : (
                    "-"
                );
            },
        },
        {
            field: "available_stock",
            headerName: "Available",
            width: 100,
            renderCell: (params) => {
                const stock = stockData[params.row.product_id];
                if (!stock) return "-";

                const available =
                    stock.available_quantity ??
                    Math.max(stock.quantity - stock.reserved_quantity, 0);
                return (
                    <Typography
                        variant="body2"
                        fontWeight="medium"
                        color={
                            available <= stock.min_stock_level
                                ? "error.main"
                                : "success.main"
                        }
                    >
                        {available}
                    </Typography>
                );
            },
        },
        {
            field: "min_level",
            headerName: "Min Level",
            width: 100,
            renderCell: (params) => {
                const stock = stockData[params.row.product_id];
                return stock ? stock.min_stock_level : "-";
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Box>
                    {canWrite && (
                        <Tooltip title="Adjust Stock">
                            <IconButton
                                size="small"
                                onClick={() => handleAdjustStock(params.row)}
                                color="primary"
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="View History">
                        <IconButton size="small" color="default">
                            <History />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    const rows = (products || []).map((product) => ({
        id: product.product_id,
        ...product,
    }));

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 3,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                >
                    Stock Management
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    {/* Search Input */}
                    <TextField
                        inputRef={searchInputRef}
                        placeholder={
                            isMobile
                                ? "Search by name, SKU, or barcode..."
                                : "Search products (name, SKU, barcode, category, brand)..."
                        }
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{
                            minWidth: { xs: 0, sm: 350 },
                            width: { xs: "100%", sm: "auto" },
                        }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchTerm && (
                                        <Button
                                            size="small"
                                            onClick={handleClearSearch}
                                            disabled={loading}
                                            sx={{ minWidth: "auto", p: 0.5 }}
                                        >
                                            ×
                                        </Button>
                                    )}
                                    <Tooltip title="Scan barcode with camera">
                                        <IconButton
                                            size="small"
                                            onClick={() => setBarcodeScannerOpen(true)}
                                            edge="end"
                                        >
                                            <CameraIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                        helperText={
                            loading
                                ? "Searching..."
                                : searchTerm.length > 0
                                  ? `Found ${products.length} product${
                                        products.length !== 1 ? "s" : ""
                                    }`
                                  : ""
                        }
                    />
                </Box>
            </Box>
            {/* Stock Summary Cards */}
            <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Inventory color="primary" />
                                <Box>
                                    <Typography variant="h6">
                                        {products.length}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Total Products
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Warning color="warning" />
                                <Box>
                                    <Typography
                                        variant="h6"
                                        color="warning.main"
                                    >
                                        {lowStockItems.length}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Low Stock Items
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <TrendingUp color="success" />
                                <Box>
                                    <Typography variant="h6">
                                        {Object.values(stockData).reduce(
                                            (sum, stock) =>
                                                sum + stock.quantity,
                                            0,
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Total Units
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <TrendingDown color="warning" />
                                <Box>
                                    <Typography
                                        variant="h6"
                                        color="warning.main"
                                    >
                                        {Object.values(stockData).reduce(
                                            (sum, stock) =>
                                                sum + stock.reserved_quantity,
                                            0,
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Reserved Units
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {lowStockItems.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {lowStockItems.length} products are running low on
                        stock:
                    </Typography>
                    {(lowStockItems || []).slice(0, 3).map((item) => (
                        <Typography key={item.product_id} variant="body2">
                            • {item.product_name} ({item.available_quantity}{" "}
                            available, min: {item.min_stock_level})
                        </Typography>
                    ))}
                    {lowStockItems.length > 3 && (
                        <Typography variant="body2">
                            ...and {lowStockItems.length - 3} more
                        </Typography>
                    )}
                </Alert>
            )}

            {isMobile ? (
                <Box sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : products.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">
                                No products found.
                            </Typography>
                        </Box>
                    ) : (
                        (pagedProducts as any[]).map((product) => {
                            const stock = stockData[product.product_id];
                            const status = getStockStatus(product);
                            const available = stock
                                ? (stock.available_quantity ??
                                  Math.max(
                                      stock.quantity - stock.reserved_quantity,
                                      0,
                                  ))
                                : null;

                            return (
                                <Card
                                    key={product.product_id}
                                    variant="outlined"
                                    sx={{
                                        mb: 1.5,
                                        borderRadius: 2,
                                        borderColor:
                                            status.color === "error"
                                                ? "error.light"
                                                : status.color === "warning"
                                                  ? "warning.light"
                                                  : "divider",
                                    }}
                                >
                                    <CardContent sx={{ pb: "8px !important" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1.5,
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <Avatar
                                                src={
                                                    (product as any).images?.[0]
                                                        ?.image_url
                                                }
                                                alt={product.name}
                                                variant="rounded"
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    flexShrink: 0,
                                                    bgcolor: "grey.100",
                                                }}
                                            >
                                                <Inventory
                                                    sx={{
                                                        color: "grey.400",
                                                    }}
                                                />
                                            </Avatar>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        alignItems:
                                                            "flex-start",
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight="bold"
                                                        sx={{
                                                            flex: 1,
                                                            mr: 1,
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {product.name}
                                                    </Typography>
                                                    <Chip
                                                        label={status.label}
                                                        color={
                                                            status.color as any
                                                        }
                                                        size="small"
                                                        sx={{
                                                            flexShrink: 0,
                                                            height: 20,
                                                            fontSize: "0.65rem",
                                                        }}
                                                    />
                                                </Box>

                                                {product.sku && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: "block",
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        SKU: {product.sku}
                                                    </Typography>
                                                )}

                                                {stock ? (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            gap: 1.5,
                                                            flexWrap: "wrap",
                                                            mt: 0.25,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Stock:{" "}
                                                            <strong>
                                                                {stock.quantity}
                                                            </strong>
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="warning.main"
                                                        >
                                                            Reserved:{" "}
                                                            <strong>
                                                                {
                                                                    stock.reserved_quantity
                                                                }
                                                            </strong>
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color={
                                                                available !==
                                                                    null &&
                                                                available <=
                                                                    stock.min_stock_level
                                                                    ? "error.main"
                                                                    : "success.main"
                                                            }
                                                        >
                                                            Available:{" "}
                                                            <strong>
                                                                {available}
                                                            </strong>
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Min:{" "}
                                                            {
                                                                stock.min_stock_level
                                                            }
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        No stock data
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </CardContent>

                                    <Divider />

                                    <CardActions
                                        sx={{ px: 1.5, py: 1, gap: 1 }}
                                    >
                                        {canWrite && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<Edit />}
                                                onClick={() =>
                                                    handleAdjustStock(product)
                                                }
                                                sx={{ flex: 1 }}
                                            >
                                                Adjust Stock
                                            </Button>
                                        )}
                                        <Tooltip title="View History">
                                            <span style={{ flex: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<History />}
                                                    disabled
                                                    fullWidth
                                                >
                                                    History
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </CardActions>
                                </Card>
                            );
                        })
                    )}
                    <MobilePaginationControls
                        page={mobilePage}
                        totalPages={mobileTotalPages}
                        canGoNext={mobileCanGoNext}
                        canGoPrev={mobileCanGoPrev}
                        onNext={mobileGoToNext}
                        onPrev={mobileGoToPrev}
                        startIndex={mobileStartIndex}
                        endIndex={mobileEndIndex}
                        total={mobileTotal}
                    />
                </Box>
            ) : (
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pageSize={50}
                    disableSelectionOnClick
                    hideActions={true}
                />
            )}

            <StockAdjustmentDialog
                open={adjustmentDialogOpen}
                onClose={() => setAdjustmentDialogOpen(false)}
                product={selectedProduct}
                currentStock={
                    selectedProduct
                        ? stockData[selectedProduct.product_id]?.quantity || 0
                        : 0
                }
                onSuccess={refreshData}
            />

            <BarcodeScannerDialog
                open={barcodeScannerOpen}
                onClose={() => setBarcodeScannerOpen(false)}
                onScan={(barcode) => setSearchTerm(barcode)}
                title="Scan Product Barcode"
            />
        </Box>
    );
};
